/**
 * Die Suche in der Kopfzeile. Woher die Treffer kommen und warum, steht in
 * data/suche.js.
 */
import { kategorieFinden, classLabel } from '../map/poiCategories.js';
import { vorschlaege, nominatim, ortsname, ortsdetail } from '../data/suche.js';
import { einpassen } from '../map/grenzen.js';
import { listenTasten } from './ortsfeld.js';

/** Unterhalb davon führen die Kacheln keine Punkte von Interesse mehr. */
const POI_AB_ZOOM = 13;

/** Drei Zeichen genügen erst ab hier - "Au" wäre sonst halb Österreich. */
const MIN_ZEICHEN = 3;

export function createSearchBox({ map, input, clearBtn, resultList, onPick, onKategorie, zuletzt }) {
  let timer;
  let controller;
  let items = [];
  /** Für welchen Text die Liste gerade gilt - damit Enter nicht Altes nimmt. */
  let listeFuer = '';

  const meter = (a, b) => {
    const R = 6371008.8, r = (d) => (d * Math.PI) / 180;
    const x = r(b.lng - a.lng) * Math.cos(r((a.lat + b.lat) / 2));
    return Math.hypot(x, r(b.lat - a.lat)) * R;
  };
  const kurz = (m) => (m < 1000 ? `${Math.round(m / 10) * 10} m` : `${(m / 1000).toFixed(1)} km`);

  /**
   * Alle Punkte einer Kategorie im aktuellen Bild.
   *
   * Bewusst `querySourceFeatures` statt `queryRenderedFeatures`: Letzteres
   * kennt nur, was auch gezeichnet wurde - und bei dichter Beschriftung
   * verdrängt MapLibre die Hälfte. Gesucht ist aber, was *da* ist.
   */
  function imBild(classes) {
    const erlaubt = new Set(classes);
    const b = map.getBounds();
    const mitte = map.getCenter();
    const gesehen = new Set();
    const treffer = [];
    for (const f of map.querySourceFeatures('osm', { sourceLayer: 'poi' })) {
      const p = f.properties || {};
      if (!erlaubt.has(p.class)) continue;
      const name = p.name || p['name:de'] || p.name_de;
      if (!name) continue;
      const g = f.geometry;
      if (g?.type !== 'Point') continue;
      const [lng, lat] = g.coordinates;
      if (lng < b.getWest() || lng > b.getEast() || lat < b.getSouth() || lat > b.getNorth()) continue;
      const schluessel = f.id ?? `${name}@${lng.toFixed(5)},${lat.toFixed(5)}`;
      if (gesehen.has(schluessel)) continue;
      gesehen.add(schluessel);
      treffer.push({
        name, lat, lng,
        kachelId: f.id,
        klasse: p.class,
        // OpenMapTiles führt z.B. Ladestationen unter der Klasse "fuel".
        // Für die Beschriftung ist die Unterklasse die ehrlichere Angabe.
        unterklasse: p.subclass,
        abstand: meter(mitte, { lat, lng })
      });
    }
    return treffer.sort((a, b2) => a.abstand - b2.abstand);
  }

  function renderKategorie(kat, liste) {
    resultList.textContent = '';
    const kopf = document.createElement('li');
    kopf.className = 'is-head';
    kopf.textContent = liste.length
      ? `${kat.label} im Kartenausschnitt (${liste.length})`
      : `${kat.label}: nichts im Kartenausschnitt`;
    resultList.append(kopf);

    if (!liste.length && map.getZoom() < POI_AB_ZOOM) {
      const hinweis = document.createElement('li');
      hinweis.className = 'is-empty';
      hinweis.textContent = 'Zu weit draussen - näher heranzoomen.';
      resultList.append(hinweis);
    }

    for (const t of liste.slice(0, 40)) {
      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.tabIndex = -1;
      btn.setAttribute('role', 'option');
      btn.innerHTML = '<strong></strong><small></small>';
      btn.querySelector('strong').textContent = t.name;
      const art = t.unterklasse && t.unterklasse !== t.klasse
        ? classLabel(t.unterklasse)
        : classLabel(t.klasse);
      btn.querySelector('small').textContent = `${art} · ${kurz(t.abstand)}`;
      btn.addEventListener('click', () => {
        map.flyTo({ center: [t.lng, t.lat], zoom: Math.max(map.getZoom(), 17), duration: 700 });
        // Den Suchbegriff stehen lassen: die Einschränkung gilt weiter, und
        // beim nächsten Verschieben zählt die Liste neu durch.
        hide();
        onPick?.({ name: t.name, lat: t.lat, lng: t.lng, display: t.name,
          kachelId: t.kachelId, klasse: t.klasse, art: t.klasse });
      });
      li.append(btn);
      resultList.append(li);
    }
    aufklappen();
  }

  function aufklappen() {
    resultList.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  const hide = () => {
    resultList.hidden = true;
    resultList.textContent = '';
    items = [];
    listeFuer = '';
    input.removeAttribute('aria-activedescendant');
    input.setAttribute('aria-expanded', 'false');
  };

  /** Von aussen aufräumen - etwa wenn der Filter die Kategorie aufhebt. */
  function leeren() {
    input.value = '';
    hide();
    punkteLoeschen();
  }

  /* ------------------------------------------------ Alle Treffer in der Karte */

  const PUNKT_QUELLE = 'suchtreffer';
  const PUNKT_EBENE = 'suchtreffer-punkt';
  const BESCHRIFTUNG = 'suchtreffer-name';
  let gezeigt = [];

  function punkteLoeschen() {
    gezeigt = [];
    for (const e of [BESCHRIFTUNG, PUNKT_EBENE]) if (map.getLayer(e)) map.removeLayer(e);
    if (map.getSource(PUNKT_QUELLE)) map.removeSource(PUNKT_QUELLE);
  }

  /**
   * Alle Treffer auf einmal: "coop luzern" meint selten *einen* Coop.
   * Statt sich durch die Liste zu klicken, sieht man die Filialen im
   * Zusammenhang - und tippt die an, die am Weg liegt.
   */
  function punkteZeigen(list) {
    punkteLoeschen();
    const punkte = list
      .map((r, i) => ({ r, i }))
      .filter(({ r }) => Number.isFinite(Number(r.lat)) && Number.isFinite(Number(r.lon)));
    if (punkte.length < 2) return;
    gezeigt = list;

    map.addSource(PUNKT_QUELLE, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: punkte.map(({ r, i }) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [Number(r.lon), Number(r.lat)] },
          properties: { nr: i, name: ortsname(r), zusatz: r.address?.road || '' }
        }))
      }
    });
    map.addLayer({
      id: PUNKT_EBENE,
      type: 'circle',
      source: PUNKT_QUELLE,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 5, 16, 8],
        'circle-color': '#C81818',
        'circle-stroke-color': '#FFFFFF',
        'circle-stroke-width': 2
      }
    });
    map.addLayer({
      id: BESCHRIFTUNG,
      type: 'symbol',
      source: PUNKT_QUELLE,
      minzoom: 13,
      layout: {
        'text-field': ['coalesce', ['get', 'zusatz'], ['get', 'name']],
        'text-font': ['Roboto Condensed Medium'],
        'text-size': 11,
        'text-offset': [0, 1.1],
        'text-anchor': 'top',
        'text-optional': true
      },
      paint: {
        'text-color': '#141414',
        'text-halo-color': '#FFFFFF',
        'text-halo-width': 1.4
      }
    });

    einpassen(map, punkte.map(({ r }) => [Number(r.lon), Number(r.lat)]), { maxZoom: 16, duration: 800 });
  }

  map.on('click', PUNKT_EBENE, (e) => {
    const nr = e.features?.[0]?.properties?.nr;
    const r = gezeigt[Number(nr)];
    if (r) pick(r, { behalten: true });
  });
  map.on('mouseenter', PUNKT_EBENE, () => { map.getCanvas().style.cursor = 'pointer'; });
  map.on('mouseleave', PUNKT_EBENE, () => { map.getCanvas().style.cursor = ''; });

  /** setStyle wirft eigene Quellen weg - hier kommen sie zurueck. */
  function redraw() {
    if (gezeigt.length) punkteZeigen(gezeigt);
  }

  /** Ein Eintrag der Liste. `tabIndex -1`: geführt wird mit den Pfeiltasten im Feld. */
  function eintrag({ kopf, zeile, klasse, onClick }) {
    const li = document.createElement('li');
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.tabIndex = -1;
    btn.setAttribute('role', 'option');
    if (klasse) btn.className = klasse;
    btn.innerHTML = '<strong></strong><small></small>';
    btn.querySelector('strong').textContent = kopf;
    btn.querySelector('small').textContent = zeile || '';
    btn.addEventListener('click', onClick);
    li.append(btn);
    return li;
  }

  function render(list, { genau = false } = {}) {
    resultList.textContent = '';
    if (!list.length) {
      const li = document.createElement('li');
      li.className = 'is-empty';
      li.textContent = 'Nichts gefunden.';
      resultList.append(li);
    }
    // Ganz oben die Sammelantwort: "coop luzern" meint meist alle, nicht einen.
    if (list.length > 1) {
      resultList.append(eintrag({
        kopf: `Alle ${list.length} Treffer zeigen`,
        zeile: input.value.trim(),
        klasse: 'is-alle',
        onClick: () => { punkteZeigen(list); hide(); }
      }));
    }
    for (const r of list) {
      const kopf = ortsname(r);
      resultList.append(eintrag({ kopf, zeile: ortsdetail(r, kopf), onClick: () => pick(r) }));
    }
    // Die Vorschläge sind schnell, aber nicht vollständig. Wer nicht findet, was
    // er sucht, kann ausführlich fragen - das ist die Suche, die Nominatim erlaubt.
    if (!genau) {
      const q = input.value.trim();
      resultList.append(eintrag({
        kopf: 'Ausführlicher suchen',
        zeile: `„${q}“ bei Nominatim`,
        klasse: 'is-mehr',
        onClick: () => absenden(q)
      }));
    }
    aufklappen();
  }

  /** Beim Antippen des leeren Felds: wohin man zuletzt wollte. */
  function zuletztZeigen() {
    const orte = zuletzt?.() || [];
    if (!orte.length || input.value.trim()) return;
    resultList.textContent = '';
    const kopf = document.createElement('li');
    kopf.className = 'is-head';
    kopf.textContent = 'Zuletzt angesehen';
    resultList.append(kopf);
    for (const o of orte) {
      resultList.append(eintrag({
        kopf: o.name,
        zeile: o.art || '',
        onClick: () => {
          hide();
          onPick?.({ ...o, display: o.name });
          map.flyTo({ center: [o.lng, o.lat], zoom: Math.max(map.getZoom(), 15), duration: 700 });
        }
      }));
    }
    aufklappen();
  }

  /** Die ausführliche Suche; mit `ersten` geht sie gleich zum besten Treffer. */
  async function absenden(q, { ersten = false } = {}) {
    clearTimeout(timer);
    controller?.abort();
    controller = new AbortController();
    try {
      items = await nominatim(q, { map, signal: controller.signal });
      listeFuer = q;
      if (ersten && items.length) pick(items[0]);
      else render(items, { genau: true });
    } catch (err) {
      if (err.name !== 'AbortError') meldung('Suche nicht erreichbar.');
    }
  }

  function meldung(text) {
    resultList.textContent = '';
    const li = document.createElement('li');
    li.className = 'is-empty';
    li.textContent = text;
    resultList.append(li);
    aufklappen();
  }

  function pick(r, { behalten = false } = {}) {
    // Aus der Liste heraus beginnt eine neue Auswahl - die alten Punkte
    // gehoeren dann nicht mehr dazu. Aus der Karte heraus bleiben sie stehen.
    if (!behalten) punkteLoeschen();
    const lat = Number(r.lat);
    const lng = Number(r.lon);
    input.value = ortsname(r, { mitOrt: true });
    clearBtn.hidden = false;
    hide();
    // Erst die Auskunft, dann der Flug: die Spalte verkleinert die Karte, und
    // sonst passt das Einpassen auf eine Fläche, die es gleich nicht mehr gibt.
    onPick?.({
      name: input.value, lat, lng, display: r.display_name,
      // Fuer die Auskunft: damit sie dieselben Einzelheiten nachladen kann
      // wie bei einem Klick in die Karte.
      osmType: r.osm_type, osmId: r.osm_id,
      art: r.addresstype || r.type, klasse: r.type
    });
    requestAnimationFrame(() => {
      map.resize();
      if (r.boundingbox) {
        const [s, n, w, e] = r.boundingbox.map(Number);
        map.fitBounds([[w, s], [e, n]], { padding: 40, maxZoom: 17, duration: 800 });
      } else {
        map.flyTo({ center: [lng, lat], zoom: Math.max(map.getZoom(), 16), duration: 800 });
      }
    });
  }

  async function run(q) {
    controller?.abort();
    controller = new AbortController();

    // Erst schauen, ob eine Kategorie gemeint ist - das beantwortet die Frage
    // aus den eigenen Kacheln, ohne einen fremden Dienst zu fragen.
    const kat = kategorieFinden(q);
    if (kat) {
      // Die Karte zeigt ab jetzt nur noch diese Art - sonst sucht man den
      // einen Treffer zwischen hundert fremden Symbolen.
      onKategorie?.(kat);
      renderKategorie(kat, imBild(kat.classes));
      return;
    }
    onKategorie?.(null);
    try {
      items = await vorschlaege(q, { map, signal: controller.signal });
      listeFuer = q;
      render(items);
    } catch (err) {
      if (err.name !== 'AbortError') meldung('Suche nicht erreichbar.');
    }
  }

  input.addEventListener('input', () => {
    const q = input.value.trim();
    clearBtn.hidden = !q;
    clearTimeout(timer);
    controller?.abort();
    if (!q) { hide(); zuletztZeigen(); return; }
    if (q.length < MIN_ZEICHEN) return hide();
    timer = setTimeout(() => run(q), 250);
  });

  input.addEventListener('focus', () => {
    if (!input.value.trim()) zuletztZeigen();
  });

  listenTasten(input, resultList, {
    onEscape: () => { hide(); input.blur(); },
    /**
     * Enter ohne markierten Eintrag. Passt die Liste zum Text, gilt ihr erster
     * Treffer. Sonst war man schneller als die Vorschläge - früher tat Enter dann
     * einfach nichts. Jetzt geht die Suche sofort los und führt zum besten Treffer.
     */
    onEnter: () => {
      const q = input.value.trim();
      if (q.length < 2) return;
      if (kategorieFinden(q)) { run(q); return; }
      if (q === listeFuer && items.length) pick(items[0]);
      else absenden(q, { ersten: true });
    }
  });

  clearBtn.addEventListener('click', () => {
    input.value = '';
    clearBtn.hidden = true;
    hide();
    punkteLoeschen();
    // Mit dem Suchbegriff geht auch die Einschränkung der Karte weg.
    onKategorie?.(null);
    input.focus();
  });

  document.addEventListener('click', (e) => {
    if (!resultList.hidden && !e.target.closest('.search')) hide();
  });

  return { hide, leeren, redraw };
}
